/**
 * Gap Analysis API Service
 * Handles all API calls for gap analysis and MITRE coverage data
 */

import { getAuthHeadersSync } from '@shared/utils/authHelpers';
import {
  MitreGapReport,
  CoverageStatistics,
  TechniqueGap,
  AlertCluster,
  TrendData,
  CoverageHeatmapCell
} from '../types';

export const gapAnalysisApi = {
  /**
   * Get comprehensive MITRE gap report
   */
  async getGapReport(
    tenantId: string,
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d'
  ): Promise<MitreGapReport> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/mitre-report?timeRange=${timeRange}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching gap report:', error);
      throw error;
    }
  },

  /**
   * Get coverage heatmap data
   */
  async getCoverageHeatmap(
    tenantId: string,
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d'
  ): Promise<CoverageHeatmapCell[]> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/heatmap?timeRange=${timeRange}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching coverage heatmap:', error);
      throw error;
    }
  },

  /**
   * Get uncovered alerts trend data
   */
  async getUncoveredAlertsTrend(
    tenantId: string,
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d'
  ): Promise<TrendData[]> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/trends?timeRange=${timeRange}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching uncovered alerts trend:', error);
      throw error;
    }
  },

  /**
   * Get alert clusters
   */
  async getAlertClusters(
    tenantId: string,
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d'
  ): Promise<AlertCluster[]> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/clusters?timeRange=${timeRange}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching alert clusters:', error);
      throw error;
    }
  },

  /**
   * Get coverage statistics
   */
  async getCoverageStatistics(
    tenantId: string,
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d'
  ): Promise<CoverageStatistics> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/statistics?timeRange=${timeRange}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching coverage statistics:', error);
      throw error;
    }
  },

  /**
   * Get specific technique gap details
   */
  async getTechniqueGapDetails(
    tenantId: string,
    techniqueId: string
  ): Promise<TechniqueGap> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/techniques/${techniqueId}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching technique details:', error);
      throw error;
    }
  },

  /**
   * Get critical gaps sorted by priority
   */
  async getCriticalGaps(
    tenantId: string,
    limit: number = 10
  ): Promise<TechniqueGap[]> {
    try {
      const response = await fetch(
        `/api/gap-analysis/${tenantId}/critical-gaps?limit=${limit}`,
        {
          method: 'GET',
          headers: getAuthHeadersSync()
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching critical gaps:', error);
      throw error;
    }
  }
};
